import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import TrustBar from '../../components/feature/TrustBar';
import ProcessSection from '../../components/feature/ProcessSection';
import WhyChooseUs from '../../components/feature/WhyChooseUs';
import ServiceAreasSection from '../../components/feature/ServiceAreasSection';
import TestimonialsSection from '../../components/feature/TestimonialsSection';
import FAQSection from '../../components/feature/FAQSection';
import FinalCTASection from '../../components/feature/FinalCTASection';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { aboutFaqs } from '../../mocks/faq';

/* ─── Data ─── */
const TEAM = [
  {
    name: 'Patty Perez',
    title: 'President',
    image: 'https://primefacilityservicesgroup.com/Team-Profile-Pictures/team-patty-perez.png',
    bio: 'Leading Prime FSG with visionary strategy and an unwavering commitment to exceptional service delivery across all divisions.',
  },
  {
    name: 'Rafael Perez',
    title: 'Senior Vice President',
    image: 'https://primefacilityservicesgroup.com/Team-Profile-Pictures/team-rafael-perez.png',
    bio: 'Driving operational excellence and strategic growth initiatives that consistently expand our reach and elevate service quality.',
  },
  {
    name: 'Sergio Biolley',
    title: 'Director of Operations',
    image: 'https://primefacilityservicesgroup.com/Team-Profile-Pictures/team-sergio-biolley.png',
    bio: 'Ensuring seamless, high-quality service delivery and maintaining the rigorous operational standards Prime is known for.',
  },
];

const CAPABILITIES = [
  { icon: 'ri-building-line', name: 'Commercial Cleaning', slug: '/services/commercial-cleaning', desc: 'Professional facility-wide cleaning' },
  { icon: 'ri-brush-line', name: 'Janitorial Services', slug: '/services/janitorial', desc: 'Recurring maintenance programs' },
  { icon: 'ri-computer-line', name: 'Office Cleaning', slug: '/services/office-cleaning', desc: 'Workspace & desk cleaning' },
  { icon: 'ri-layout-bottom-line', name: 'Floor Care & Maintenance', slug: '/services/floor-care', desc: 'Stripping, waxing & polishing' },
  { icon: 'ri-building-2-line', name: 'Post-Construction Cleaning', slug: '/services/post-construction', desc: 'Post-build deep cleaning' },
  { icon: 'ri-sparkling-line', name: 'Specialized Cleaning', slug: '/services/specialized', desc: 'Custom facility solutions' },
  { icon: 'ri-team-line', name: 'Professional Staffing', slug: '/services/staffing', desc: 'Elite hospitality professionals' },
  { icon: 'ri-car-line', name: 'Valet Parking', slug: '/services/valet', desc: 'Premium parking solutions' },
];

/* ─── 1. Hero ─── */
function AboutHero() {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <section className="relative min-h-[70vh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/207c264eb4c0dba83ba3dc5ce097b731.jpg"
          alt="About Prime Facility Services Group Houston TX"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#03143A]/95 via-[#03143A]/80 to-[#03143A]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#03143A]/50 via-transparent to-[#03143A]/20" />
      </div>
      <div className="absolute left-0 inset-y-0 w-1 bg-gradient-to-b from-transparent via-[#C70532] to-transparent opacity-60" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-36 pb-24 w-full">
        <div ref={ref} className={`max-w-3xl transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <nav className="flex items-center gap-2 text-white/50 text-sm mb-6">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white font-medium">About Us</span>
          </nav>
          <span className="inline-block px-5 py-2 bg-[#C70532]/20 border border-[#C70532]/30 text-[#C70532] text-xs font-bold uppercase tracking-widest rounded-full mb-6">Our Story</span>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
            About Prime Facility Services Group
          </h1>
          <p className="text-xl text-white/70 max-w-2xl leading-relaxed mb-10">
            Houston&apos;s premier commercial cleaning and facility management company — serving 500+ businesses with professionalism, reliability, and an unwavering commitment to excellence since 2008.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" className="px-8 py-4 bg-[#C70532] hover:bg-[#a80428] text-white font-bold rounded-full transition-all cursor-pointer whitespace-nowrap">
              Work With Us
            </Link>
            <a href="tel:+17133382553" className="flex items-center gap-2 px-8 py-4 bg-white/10 border-2 border-white/25 hover:border-white text-white font-bold rounded-full transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-[#C70532]" />
              (713) 338-2553
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── 3. Company Capabilities (Services Overview equivalent) ─── */
function CompanyCapabilities() {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-5 py-2 bg-[#C70532]/10 text-[#C70532] text-xs font-bold uppercase tracking-widest rounded-full mb-5">Our Capabilities</span>
          <h2 className="text-4xl md:text-5xl font-black text-[#03143A] mb-5 leading-tight">Full-Service Facility Management</h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">From commercial cleaning to professional staffing and valet services — we handle everything your facility needs under one trusted partner.</p>
        </div>

        {/* Company overview - two column */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-black text-[#03143A] mb-5 leading-tight">Houston&apos;s Most Trusted Facility Services Partner</h3>
            <p className="text-gray-600 text-lg leading-relaxed mb-5">
              <strong>Prime Facility Services Group</strong> is a full-service commercial cleaning and facility management company headquartered in Houston, Texas. With over 15 years of industry experience, we serve businesses across the Greater Houston area with professionalism, reliability, and a deep commitment to quality.
            </p>
            <p className="text-gray-500 leading-relaxed mb-8">
              From routine janitorial maintenance to specialized facility solutions, our team of trained and vetted professionals delivers consistent results that protect your brand and create outstanding environments for employees and clients alike.
            </p>
            <div className="grid grid-cols-2 gap-4">
              {[
                { stat: '15+', label: 'Years in Business' },
                { stat: '500+', label: 'Active Clients' },
                { stat: '98%', label: 'Satisfaction Rate' },
                { stat: '2,500+', label: 'Staff Placed' },
              ].map((item) => (
                <div key={item.stat} className="bg-[#FAF8F5] rounded-2xl p-5 text-center border border-gray-100">
                  <div className="text-3xl font-black text-[#C70532] mb-1">{item.stat}</div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="bg-[#03143A] rounded-3xl p-8 mb-6">
              <h4 className="text-xl font-bold text-white mb-3">Our Mission</h4>
              <p className="text-white/70 leading-relaxed text-sm mb-6">To deliver consistent, high-quality cleaning and facility services that help businesses across Houston maintain clean, safe, and professional environments — every single day.</p>
              <h4 className="text-xl font-bold text-white mb-3">Our Vision</h4>
              <p className="text-white/70 leading-relaxed text-sm">To be the most trusted facility services partner in Greater Houston, recognized for unmatched reliability, professionalism, and a genuine commitment to our clients&apos; success.</p>
            </div>

            {/* Leadership team mini */}
            <div className="grid grid-cols-3 gap-3">
              {TEAM.map((member) => (
                <div key={member.name} className="group rounded-2xl overflow-hidden border border-gray-100 bg-[#FAF8F5]">
                  <div className="h-28 overflow-hidden">
                    <img src={member.image} alt={member.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-3 text-center">
                    <p className="font-bold text-[#03143A] text-xs leading-snug">{member.name}</p>
                    <p className="text-[#C70532] text-xs">{member.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Capabilities grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {CAPABILITIES.map((cap, i) => (
            <Link
              key={cap.slug}
              to={cap.slug}
              className={`flex items-center gap-4 p-5 bg-[#FAF8F5] hover:bg-[#03143A] border border-gray-100 hover:border-[#03143A] rounded-2xl transition-all duration-300 group cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 50}ms` }}
            >
              <div className="w-11 h-11 flex items-center justify-center bg-[#03143A] group-hover:bg-[#C70532] rounded-xl flex-shrink-0 transition-colors duration-300">
                <i className={`${cap.icon} text-white text-lg`} />
              </div>
              <div>
                <span className="font-bold text-[#03143A] group-hover:text-white transition-colors text-sm block">{cap.name}</span>
                <span className="text-gray-400 group-hover:text-white/60 transition-colors text-xs">{cap.desc}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 4. About AI Answer Block ─── */
function AboutAIBlock() {
  const { ref, isVisible } = useScrollAnimation();
  const ANSWERS = [
    {
      icon: 'ri-information-line',
      title: 'Who is Prime Facility Services Group?',
      body: 'Prime Facility Services Group is a professional commercial cleaning and facility services provider dedicated to helping businesses across Houston maintain clean, safe, and professional environments. Founded with a commitment to reliability and excellence, we\'ve grown to serve 500+ clients throughout Greater Houston.',
    },
    {
      icon: 'ri-award-line',
      title: 'What is their mission?',
      body: 'Their mission is to deliver consistent, high-quality cleaning and facility management services that help Houston businesses maintain spotless, compliant, and professional environments every single day — while building lasting partnerships built on trust, reliability, and genuine care.',
    },
    {
      icon: 'ri-group-line',
      title: 'Who do they serve?',
      body: 'They serve businesses of all sizes across Greater Houston — from corporate office buildings and medical facilities to hotels, retail stores, restaurants, schools, industrial warehouses, and event venues. Any business that needs a consistently clean, safe professional environment is an ideal client.',
    },
    {
      icon: 'ri-flow-chart',
      title: 'How does their process work?',
      body: 'The process starts with a free facility assessment to understand your needs and challenges. They then build a custom cleaning plan tailored to your schedule and budget, execute consistently with trained staff, and maintain quality through regular supervisory inspections and client feedback.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Are they licensed and insured?',
      body: 'Yes. Prime Facility Services Group is fully licensed, bonded, and insured. They carry comprehensive general liability and workers\' compensation insurance. All staff members undergo thorough background screening and professional training before working at any client facility.',
    },
  ];

  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="py-24 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className={`grid lg:grid-cols-2 gap-16 items-start transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <span className="inline-block px-5 py-2 bg-[#C70532]/10 text-[#C70532] text-xs font-bold uppercase tracking-widest rounded-full mb-6">Know More</span>
            <h2 className="text-4xl md:text-5xl font-black text-[#03143A] mb-6 leading-tight">
              Who Is Prime Facility Services Group?
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              <strong className="text-[#03143A]">Prime Facility Services Group</strong> is a full-service commercial cleaning and facility management company based in Houston, Texas. Since 2008, we&apos;ve been delivering professional, consistent, and detail-driven services to businesses throughout the Greater Houston area.
            </p>
            <p className="text-gray-500 leading-relaxed mb-8">
              Our company was founded on the belief that every business deserves a clean, safe, and professional environment — and that achieving that standard requires a dedicated partner who genuinely cares about your facility&apos;s success.
            </p>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: 'ri-map-pin-2-line', label: 'Houston, TX Based' },
                { icon: 'ri-shield-check-line', label: 'Licensed & Insured' },
                { icon: 'ri-group-line', label: '2,500+ Staff Placed' },
                { icon: 'ri-star-fill', label: '4.8★ Google Rating' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#C70532]/10 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-[#C70532]`} />
                  </div>
                  <span className="font-semibold text-[#03143A] text-sm">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{ transitionDelay: '200ms' }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center bg-[#C70532] rounded-xl">
                <i className="ri-robot-2-line text-white" />
              </div>
              <div>
                <p className="font-bold text-[#03143A] text-sm">AI Answer Block</p>
                <p className="text-gray-400 text-xs">Optimized for search &amp; voice queries</p>
              </div>
            </div>
            <div className="space-y-3">
              {ANSWERS.map((item, i) => (
                <div key={i} className={`rounded-2xl border overflow-hidden transition-all duration-300 ${open === i ? 'border-[#C70532]/30 bg-white' : 'border-gray-200 bg-white'}`}>
                  <button
                    className="w-full flex items-center gap-4 px-6 py-5 text-left cursor-pointer group"
                    onClick={() => setOpen(open === i ? null : i)}
                  >
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors ${open === i ? 'bg-[#C70532]' : 'bg-[#03143A]/8 group-hover:bg-[#C70532]'}`}>
                      <i className={`${item.icon} text-sm transition-colors ${open === i ? 'text-white' : 'text-[#03143A] group-hover:text-white'}`} />
                    </div>
                    <h3 className={`font-bold text-sm flex-1 pr-2 transition-colors leading-snug ${open === i ? 'text-[#C70532]' : 'text-[#03143A] group-hover:text-[#C70532]'}`}>{item.title}</h3>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${open === i ? 'bg-[#C70532] rotate-180' : 'bg-gray-100'}`}>
                      <i className={`ri-arrow-down-s-line text-sm ${open === i ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                  </button>
                  {open === i && (
                    <div className="px-6 pb-5 pl-[4.5rem]">
                      <p className="text-gray-600 text-sm leading-relaxed">{item.body}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <>
      <Navbar />
      <main>
        {/* 1. Hero */}
        <AboutHero />
        {/* 2. Trust Bar */}
        <TrustBar />
        {/* 3. Company Capabilities (Services Overview equivalent) */}
        <CompanyCapabilities />
        {/* 4. AI Answer Block */}
        <AboutAIBlock />
        {/* 5. Process */}
        <ProcessSection
          title="How We Partner With Your Business"
          subtitle="From your first inquiry to ongoing quality maintenance — we make the entire experience seamless and reliable."
        />
        {/* 6. Why Choose Us */}
        <WhyChooseUs
          title="Why Businesses Trust Prime Facility Services Group"
          subtitle="15+ years, 500+ clients, and a reputation built on consistency, professionalism, and genuine partnership."
        />
        {/* 7. Service Areas */}
        <ServiceAreasSection
          title="Serving Businesses Across Greater Houston"
          subtitle="Prime Facility Services Group provides facility services to businesses throughout Houston and surrounding communities."
        />
        {/* 8. Testimonials */}
        <TestimonialsSection title="What Our Clients Say About Us" />
        {/* 9. FAQ */}
        <FAQSection
          faqs={aboutFaqs}
          title="Common Questions About Prime FSG"
          subtitle="Answers to the most common questions businesses ask about working with Prime Facility Services Group."
        />
        {/* 10. Final CTA */}
        <FinalCTASection
          title="Partner With Houston's Premier Facility Services Company"
          subtitle="Join 500+ businesses who trust Prime Facility Services Group for consistent, professional, and reliable results."
        />
      </main>
      <Footer />
    </>
  );
}